import { useQuery } from '@apollo/client';
import { Stadium } from '@mui/icons-material';
import { Box, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useDeleteEventType } from '~/api/event-types/delete';
import { useUpdateEventType } from '~/api/event-types/update';
import { applyServerErrors } from '~/api/helpers';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { Tooltip } from '~/components/Tooltip';
import { DeleteButton, SaveButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import { Timestamps } from '~/components/timestamps';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { EventTypeForm, eventTypeSchema, serializeEventType } from '../components/form';
import { NetworkEventTypeSettingsDocument } from '../queries/settings.generated';
import { useTabs } from './lib/tabs';

export const EventTypeSettings = () => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();

  const navigate = useNavigate();

  const link = useLink();

  const tabs = useTabs();

  const params = useParams<{ eventTypeId: string }>();

  const eventTypeId = Number(params.eventTypeId);

  const { data, loading, error } = useQuery(NetworkEventTypeSettingsDocument, {
    variables: { networkId: currentNetwork.id, eventTypeId },
  });

  const theme = useTheme();
  const [deleteEventType] = useDeleteEventType();
  const [updateEventType] = useUpdateEventType();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const eventType = data?.network?.eventType;

  const initialValues = useMemo(
    () => ({
      color: eventType?.color ?? theme.palette.primary.main,
      contentStartTime: eventType?.contentStartTime ?? 'PT0S',
      contentStopTime: eventType?.contentStopTime ?? 'PT0S',
      description: eventType?.description ?? '',
      fcLeagueId: eventType?.fcLeagueId ?? -1,
      fcTeamId: eventType?.fcTeamId ?? -1,
      iconInputUri: '',
      iconUploadUri: '',
      manageDisplay: eventType?.manageDisplay ?? true,
      name: eventType?.name ?? '',
      sponsorStartTime: eventType?.sponsorStartTime ?? 'PT0S',
      sponsorStopTime: eventType?.sponsorStopTime ?? 'PT0S',
      syncFcAdmin: eventType?.syncFcAdmin ?? false,
    }),
    [eventType, theme.palette.primary.main],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values, { setErrors }) => {
      const patch = serializeEventType(values);

      const result = await updateEventType({ variables: { patch, id: eventTypeId } });
      if (applyServerErrors(result, setErrors) || result.errors) return;

      formik.resetForm({ values });
    },
    validateOnMount: true,
    validationSchema: eventTypeSchema,
  });

  const handleDelete = useCallback(async () => {
    if (!(await confirmDelete())) return;
    await deleteEventType({
      variables: { id: eventTypeId },
    });
    navigate(link('/event-types'), { replace: true });
  }, [confirmDelete, deleteEventType, eventTypeId, link, navigate]);

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      <>
        <Toolbar
          actions={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <SaveButton
                disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                type="submit"
                form="update-event-type"
              >
                {t('common.actions.save')}
              </SaveButton>
              <Tooltip
                title={t('pages.event_types.detail.delete_tooltip', {
                  reasons: data?.network?.eventType.canDestroy.reasons?.fullMessages.join(', '),
                })}
              >
                <span>
                  <DeleteButton
                    disabled={!data?.network?.eventType.canDestroy.value}
                    onClick={handleDelete}
                  >
                    {t('common.actions.delete')}
                  </DeleteButton>
                </span>
              </Tooltip>
            </Box>
          }
          breadcrumbsLabel={<RouterBreadcrumbs />}
          titleIcon={<Stadium />}
          titleText={data?.network?.eventType.name}
        />

        <ScrollableRouterTabs current={t('common.labels.details')} tabs={tabs} />

        {data && (
          <PageContainer>
            <EventTypeForm
              formik={formik}
              formId="update-event-type"
              iconFileUri={data.network?.eventType.iconFile?.uri}
              leagues={data.network?.leagues ?? []}
              newRecord={false}
            />

            {currentUser.admin && (
              <Timestamps
                sx={{ mt: 2 }}
                createdAt={data.network?.eventType.createdAt ?? ''}
                updatedAt={data.network?.eventType.updatedAt ?? ''}
              />
            )}
          </PageContainer>
        )}

        <ConfirmDialog
          {...confirmDeleteProps}
          confirm={t('pages.event_types.detail.delete_confirm_button')}
          deleteConfirm
          prompt={
            <span
              dangerouslySetInnerHTML={{
                __html: t('pages.event_types.detail.delete_confirm_prompt'),
              }}
            />
          }
          title={t('pages.event_types.detail.delete_confirm_title')}
        />
      </>
    </LoadingPane>
  );
};
